import React, { useState, useEffect, useMemo } from "react";
import useSWR from "swr";
import BuildingIcon from "../../../svgs/building.svg";
import CustomSelect from "../../../../RevampedDashboard/components/CustomSelect";
import { useNotification } from "../../../../Notifications/NotificationProvider";
import {
  inventoryFetcher,
  InventoryService,
} from "../../../network/InventoryService";
import FormField from "../../../../Shared/Form/FormField";

const WarehouseManagerCreateModal = ({
  setShowWarehouseManagerCreateModal,
  isEditable = true,
  managerData = null,
  isEditMode = false,
  onManagerCreated,
}) => {
  const dispatchNotification = useNotification();
  const [form, setForm] = useState({
    employeeName: "",
    phoneNumber: "+880",
    email: "",
    password: "",
    selectedWarehouse: [],
    joiningDate: "",
  });
  const [formError, setFormError] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  const warehousesApiUrl = useMemo(() => {
    const params = new URLSearchParams({
      is_dropdown: true,
    });

    return `${
      process.env.REACT_APP_API_URL
    }/api/payment_speaker/v1/psb-warehouse?${params.toString()}`;
  }, []);

  const { data: warehousesResponse, error: warehousesError } = useSWR(
    warehousesApiUrl,
    inventoryFetcher,
    {
      fallbackData: {},
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  const warehouses = warehousesResponse?.data || [];
  const warehousesLoading = !warehousesResponse && !warehousesError;

  const warehouseSelectItem = useMemo(() => {
    const options = warehouses.map((warehouse) => ({
      id: warehouse.id.toString(),
      name: `${warehouse.name} - ${warehouse.address}`,
    }));

    return {
      label: "Select Warehouses",
      icon: BuildingIcon,
      value: form.selectedWarehouse.map((w) => w.id),
      options: options,
    };
  }, [warehouses, form.selectedWarehouse]);

  useEffect(() => {
    console.log("Selected Warehouse: ", managerData);
    if (managerData && isEditMode) {
      setForm({
        employeeName: managerData.name || "",
        phoneNumber: managerData.contact_number || "+880",
        email: managerData.username || "",
        password: "",
        selectedWarehouse: managerData.warehouse
          ? managerData.warehouse.map((warehouse) => ({
              id: warehouse.id.toString(),
              name: warehouse.name,
            }))
          : [],
        joiningDate: managerData.joining_date || "",
      });
    }
  }, [managerData, isEditMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleWarehouseChange = (e) => {
    const selectedWarehouseIds = Array.isArray(e.target.value)
      ? e.target.value
      : [e.target.value];

    const selectedWarehouses = warehouses
      .filter((warehouse) =>
        selectedWarehouseIds.includes(warehouse.id.toString())
      )
      .map((warehouse) => ({
        id: warehouse.id.toString(),
        name: warehouse.name,
      }));

    setForm({
      ...form,
      selectedWarehouse: selectedWarehouses,
    });
  };

  const validatePassword = (password) => {
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
    const hasMinLength = password.length >= 8;

    return (
      hasUppercase &&
      hasLowercase &&
      hasNumbers &&
      hasSpecialChar &&
      hasMinLength
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !form.employeeName ||
      !form.phoneNumber ||
      !form.email ||
      (!isEditMode && !form.password) ||
      form.selectedWarehouse.length === 0
    ) {
      setFormError("Please fill all required fields.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setFormError("Please enter a valid email address.");
      return;
    }

    if (form.phoneNumber.length < 10) {
      setFormError("Please enter a valid phone number.");
      return;
    }

    if (form.password && !validatePassword(form.password)) {
      setFormError(
        "Password must contain at least 8 characters including uppercase, lowercase, numbers, and special characters."
      );
      return;
    }

    if (!isEditMode && !validatePassword(form.password)) {
      setFormError(
        "Password must contain at least 8 characters including uppercase, lowercase, numbers, and special characters."
      );
      return;
    }

    setIsCreating(true);
    setFormError("");

    try {
      const managerRequestData = {
        name: form.employeeName,
        email: form.email,
        password: form.password,
        contact_number: form.phoneNumber,
        warehouse: form.selectedWarehouse,
        joining_date: form.joiningDate,
      };

      let response;

      if (isEditMode && managerData?.id) {
        response = await InventoryService.updateWarehouseManager(
          managerData.id,
          managerRequestData
        );
      } else {
        response =
          await InventoryService.createWarehouseManager(managerRequestData);
      }

      if (response.is_error === 0) {
        dispatchNotification({
          type: "SUCCESS",
          message: isEditMode
            ? "Warehouse manager updated successfully!"
            : "Warehouse manager created successfully!",
        });

        setForm({
          employeeName: "",
          phoneNumber: "+880",
          email: "",
          password: "",
          selectedWarehouse: [],
          joiningDate: "",
        });

        setShowWarehouseManagerCreateModal(false);

        if (onManagerCreated) {
          onManagerCreated();
        }
      } else {
        throw new Error(
          response.message ||
            `Failed to ${isEditMode ? "update" : "create"} warehouse manager`
        );
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} warehouse manager:`,
        error
      );

      let errorMessage = `Failed to ${
        isEditMode ? "update" : "create"
      } warehouse manager. Please try again.`;

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setFormError(errorMessage);

      dispatchNotification({
        type: "ERROR",
        message: errorMessage,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeactivate = async () => {
    if (!managerData?.id) return;

    setIsDeactivating(true);
    setFormError("");

    try {
      const response = await InventoryService.deactivateWarehouseManager(
        managerData.id
      );

      if (response.is_error === 0) {
        dispatchNotification({
          type: "SUCCESS",
          message: "Warehouse manager deactivated successfully!",
        });

        setShowWarehouseManagerCreateModal(false);

        if (onManagerCreated) {
          onManagerCreated();
        }
      } else {
        throw new Error(
          response.message || "Failed to deactivate warehouse manager"
        );
      }
    } catch (error) {
      console.error("Error deactivating warehouse manager:", error);

      let errorMessage =
        "Failed to deactivate warehouse manager. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setFormError(errorMessage);

      dispatchNotification({
        type: "ERROR",
        message: errorMessage,
      });
    } finally {
      setIsDeactivating(false);
    }
  };

  return (
    <div
      className="inventory-modal-overlay"
      onClick={() => !isCreating && setShowWarehouseManagerCreateModal(false)}
    >
      <form
        className="inventory-modal inventory-warehouse-create-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="inventory-modal-header">
          <h3>
            {isEditMode
              ? "Edit Warehouse Manager"
              : "Create New Warehouse Manager"}
          </h3>
          <button
            className="close-btn"
            type="button"
            onClick={() =>
              !isCreating && setShowWarehouseManagerCreateModal(false)
            }
            disabled={isCreating}
          >
            ×
          </button>
        </div>

        <div className="inventory-modal-body">
          <FormField
            id="employee-name"
            name="employeeName"
            title="Employee Name"
            type="text"
            value={form.employeeName}
            onChange={handleInputChange}
            placeholder="Employee name ..."
            disabled={!isEditable || isCreating}
            required
          />

          <FormField
            id="phone-number"
            name="phoneNumber"
            title="Phone Number / Whitelisting Number"
            type="tel"
            value={form.phoneNumber}
            onChange={handleInputChange}
            placeholder="+880"
            disabled={!isEditable || isCreating}
            required
          />

          <FormField
            id="email"
            name="email"
            title="Email"
            type="email"
            value={form.email}
            onChange={handleInputChange}
            placeholder="Email..."
            disabled={!isEditable || isCreating}
            required
          />

          <FormField
            id="password"
            name="password"
            title="Password"
            type="password"
            value={form.password}
            onChange={handleInputChange}
            placeholder={
              isEditMode
                ? "Leave blank to keep current password"
                : "Enter password for Dexter Portal..."
            }
            disabled={!isEditable || isCreating}
            required={!isEditMode}
          />

          <div className="common-form-item">
            <label className="common-form-label">Select Warehouses *</label>
            <CustomSelect
              item={warehouseSelectItem}
              handleChange={handleWarehouseChange}
              isMultiSelect={true}
            />
          </div>

          <FormField
            id="joining-date"
            name="joiningDate"
            title="Joining Date"
            type="date"
            value={form.joiningDate}
            onChange={handleInputChange}
            disabled={!isEditable || isCreating}
            required
          />

          {(warehousesError || warehousesLoading) && (
            <div className="common-form-error">
              {warehousesLoading
                ? "Loading warehouse list..."
                : "Failed to load warehouses. Please refresh the page."}
            </div>
          )}

          {formError && <div className="common-form-error">{formError}</div>}
        </div>

        {isEditable && (
          <div className="inventory-modal-footer">
            <button
              className="cancel-btn"
              type="button"
              onClick={() => setShowWarehouseManagerCreateModal(false)}
              disabled={isCreating || isDeactivating}
            >
              Cancel
            </button>
            {isEditMode && (
              <button
                className="submit-btn"
                type="button"
                onClick={handleDeactivate}
                disabled={isCreating || isDeactivating}
              >
                {isDeactivating ? "Deactivating..." : "Deactivate"}
              </button>
            )}
            <button
              className="submit-btn"
              type="submit"
              disabled={isCreating || isDeactivating}
            >
              {isCreating
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                  ? "Update"
                  : "Create"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default WarehouseManagerCreateModal;
